/**
 * L10 API — real HTTP client.
 *
 * Mirrors the function surface in ``./mocks.ts`` 1:1. The page imports
 * everything through ``./index.ts`` so flipping from mocks to client is
 * a one-line change. Function signatures are the contract; shape
 * differences between backend wire types and frontend ``./types.ts`` are
 * smoothed over here (snake_case stays, but composite shapes like
 * ``L10Session`` are assembled from the session snapshot + members
 * lookup instead of being delivered in one payload).
 *
 * Notes on a few intentional shortcuts:
 *
 * - ``getScorecardForTeam`` + ``getRocksForTeam`` return ``[]``. These
 *   embeds are deferred until the L10 backend learns how to serve a
 *   per-team scorecard / rocks view; the runner panels render an empty
 *   state cleanly in the meantime. Tracked as follow-ups.
 * - ``listCascades`` + ``listCascadeTargets`` take an optional id arg
 *   (sessionId / teamId). The two existing call sites (conclude pane
 *   + leadership page) already have these ids in scope and pass them
 *   through; calling with no arg returns ``[]`` rather than failing
 *   open, so any incremental caller that forgets is safe.
 */

import { fetchApi } from "@/lib/api";
import { KORY_CONTENT_ENCODING, encodeBase64Utf8 } from "@/lib/content-encoding";
import type { TodoDenominatorPayload } from "@/lib/l10/todo-snapshot";
import type { TodoActionDTO } from "@/hooks/use-agent-todos";

import { AGENDA_KINDS, SCORECARD_KINDS } from "./types";
import type {
  AgendaItem,
  AgendaKind,
  AssignTodoBody,
  AttendeeProfile,
  CascadeDecision,
  CopyHeadlineToBody,
  CopyIssueToBody,
  CopyTodoToBody,
  CreateCascadeBody,
  CreateHeadlineBody,
  CreateIssueBody,
  CreateTodoBody,
  Headline,
  Issue,
  IssueStatus,
  L10AgendaSection,
  L10Attendance,
  L10ItemKind,
  L10ItemLink,
  L10ItemRef,
  L10Recap,
  L10MeetingSeries,
  TeamSalesHuddle,
  MySalesHuddle,
  L10RecapSummary,
  L10Session,
  L10SessionEvent,
  L10Team,
  L10UserTodo,
  LinkedRock,
  MeetSpaceStatus,
  MeetTranscriptPullRequest,
  MoveHeadlineBody,
  MoveIssueBody,
  MoveTodoBody,
  MyL10Team,
  IdsFocus,
  PropsPhase,
  RockWeeklySnapshot,
  ScorecardKind,
  ScorecardRow,
  ScribeChunkBody,
  ScribeChunkResult,
  SessionStatus,
  SolveIssueBody,
  TranscriptPullStatus,
  UpdateHeadlineBody,
  UpdateIssueBody,
} from "./types";

// ============================================================================
// Re-exported constants (kept on the L10 surface for backwards compat with the
// mock — pages still import these from "@/api/l10")
// ============================================================================

/**
 * Hickory Leadership L10 — real ``l10_teams`` row. Hardcoded here so the
 * beta-1 ``/meetings/leadership`` page can mount without a team-picker.
 * Sprint-2 resolves teams by slug + route param.
 */
export const LEADERSHIP_TEAM_ID = "51f9d4f2-917a-455d-8e29-e4217c473533";

/**
 * Canonical EOS L10 default per-section budgets in seconds — sum = 90 min.
 * Backend mirrors this in ``app/services/l10/defaults.py`` so the merged
 * ``L10TeamRead.section_budgets_seconds`` payload always contains every
 * agenda kind; this constant is the per-section fallback when an absent
 * kind needs a render-time default (e.g., a custom team with overrides
 * for only some sections).
 */
export const DEFAULT_SECTION_BUDGETS_SECONDS: Record<AgendaKind, number> = {
  segue: 5 * 60,
  scorecard: 5 * 60,
  rocks: 5 * 60,
  headlines: 5 * 60,
  todos: 5 * 60,
  ids: 60 * 60,
  conclude: 5 * 60,
};

// ============================================================================
// Internal wire-type shims — minimum shape we read from each endpoint
// ============================================================================

interface TeamReadWire {
  id: string;
  name: string;
  recurring_day_of_week: number | null;
  recurring_time: string | null; // "HH:MM:SS"
  recurring_tz: string;
  default_duration_minutes: number;
  section_budgets_seconds: Record<string, number>;
  meet_space_code: string | null;
  scorecard_links?: { path: string; label?: string }[];
  scorecard_kind?: string;
  scorecard_week_default?: string;
  brand_scope?: string[];
  // The team's EFFECTIVE agenda (always populated server-side — derived from
  // the meeting-length template when no custom agenda is stored). Optional on
  // the wire type only so an old cached response in the deploy window maps
  // cleanly to the runner's AGENDA_KINDS fallback.
  agenda?: AgendaItem[];
  agenda_is_custom?: boolean;
  // "standard" for an ordinary weekly L10; a non-standard value (e.g.
  // "quarterly_planning") marks a hidden backing team for a different
  // meeting type. Optional on the wire only so an old cached response in the
  // deploy window maps cleanly to the "standard" fallback.
  container_kind?: string;
}

interface SessionReadWire {
  id: string;
  team_id: string;
  started_at: string;
  ended_at: string | null;
  facilitator_id: string | null;
  status: SessionStatus;
  // Conclude Props shared cursor — optional on the wire so an old backend in
  // the deploy window (before the migration runs) maps cleanly to the defaults.
  props_phase?: PropsPhase;
  props_index?: number;
  props_review_started_at?: string | null;
  // IDS shared discussion focus — optional/nullable on the wire for the same
  // deploy-window reason.
  ids_focus?: { picking?: boolean; picked?: string[]; discussing?: string[] } | null;
  // The team's effective agenda SNAPSHOTTED at session start (see the
  // backend's session read). Optional on the wire: an old backend in the
  // deploy window (or a session created before the snapshot migration)
  // simply omits it and the caller-supplied order applies.
  agenda?: AgendaItem[] | null;
}

interface AgendaSectionWire {
  id: string;
  session_id: string;
  kind: AgendaKind;
  started_at: string | null;
  ended_at: string | null;
  notes: string | null;
}

interface AttendanceWire {
  id: string;
  session_id: string;
  profile_id: string;
  rating_1_to_10: number | null;
  rating_note: string | null;
}

interface CascadeDecisionWire {
  id: string;
  source_session_id: string;
  target_team_id: string;
  // Optional on the wire — backend populates it only on the
  // create-cascade response; snapshot reads and incoming-cascade lists
  // leave it absent.
  target_team_name?: string | null;
  content: string;
  status: CascadeDecision["status"];
  delivered_session_id: string | null;
  consumed_at: string | null;
  created_at: string;
}

interface SessionSnapshotWire {
  session: SessionReadWire;
  events: L10SessionEvent[];
  sections: AgendaSectionWire[];
  attendance: AttendanceWire[];
  cascades: CascadeDecisionWire[];
}

interface HeadlineWire {
  id: string;
  team_id: string;
  author_id: string | null;
  author_user_id: string | null;
  // Author display name, attached by the read model for COPIED (cascade)
  // rows only — their author is on the source team's roster, which this
  // team's attendee lookup can never resolve. Optional so an old backend in
  // the deploy window maps cleanly to null.
  author_name?: string | null;
  kind: Headline["kind"];
  title: string;
  body: string | null;
  sentiment: Headline["sentiment"];
  cascaded_from_session_id: string | null;
  cascaded_from_team_id: string | null;
  // Source-team display name for a copied/broadcast headline, resolved by
  // the backend at read time — the "Copied from {team}" badge reads this.
  cascaded_from_team_name?: string | null;
  linked_issue_id: string | null;
  source_type: string | null;
  source_ref: string | null;
  // Kory's running recap from a recorded meeting's live text. Optional so an
  // old backend in the deploy window maps cleanly to null.
  discussion_summary?: string | null;
  // Drive artifact linkage (new this release; optional so an old backend in the
  // deploy window that omits them maps cleanly to null).
  artifact_token?: string | null;
  dedup_key?: string | null;
  // Drive feedback (the caller's own vote + the team tally). Optional so an
  // old backend in the deploy window maps cleanly to the empty state.
  my_feedback_rating?: "up" | "down" | null;
  feedback_counts?: { up: number; down: number } | null;
  dismissed_at: string | null;
  read_at: string | null;
  created_at: string;
}

interface IssueWire {
  id: string;
  tenant_id: string;
  team_id: string;
  title: string;
  description: string | null;
  author_id: string | null;
  author_user_id: string | null;
  owner_id: string | null;
  owner_user_id: string | null;
  // Read-model display names (optional so an old backend in the deploy
  // window maps cleanly to absent): the off-roster owner's name + the origin
  // team of a cross-team copy. ``issueWireToIssue`` spreads, so both carry.
  owner_name?: string | null;
  copied_from_team_name?: string | null;
  status: IssueStatus;
  time_horizon: Issue["time_horizon"];
  priority: number;
  source_type: Issue["source_type"] | null;
  source_ref: string | null;
  // The rock a source_type='rock' issue came from (carried via spread).
  linked_rock?: LinkedRock | null;
  // Drive artifact linkage (new this release; ``issueWireToIssue`` carries
  // them through via spread — declared here to make the wire contract explicit).
  artifact_token?: string | null;
  dedup_key?: string | null;
  // Drive feedback (the caller's own vote + the team tally). Optional so an
  // old backend in the deploy window maps cleanly to the empty state.
  my_feedback_rating?: "up" | "down" | null;
  feedback_counts?: { up: number; down: number } | null;
  identified_at: string | null;
  solved_at: string | null;
  solve_summary: string | null;
  solve_summary_is_suggested: boolean;
  discussion_summary: string | null;
  staged_at: string | null;
  dismissed_at: string | null;
  created_at: string;
}

interface TodoReadWire {
  id: string;
  title: string;
  notes: string;
  priority: "normal" | "high" | "critical";
  owner_user_id: string | null;
  owner_profile_id: string | null;
  owner_name: string | null;
  due_date: string | null;
  status: "pending" | "done" | "cancelled";
  source: string;
  completed_at: string | null;
  completed_by_name?: string | null;
  origin_line?: string | null;
  created_by_name?: string | null;
  reviewed_at: string | null;
  dismissed_until: string | null;
  l10_issue_id: string | null;
  l10_headline_id: string | null;
  l10_team_id: string | null;
  l10_team_name: string | null;
  created_at: string;
  delegated?: boolean;
  // The linked rock (optional so an old backend maps to null).
  linked_rock?: LinkedRock | null;
  // Drive artifact linkage (new this release; optional so an old backend in the
  // deploy window that omits them maps cleanly to null).
  artifact_token?: string | null;
  drive_dedup_key?: string | null;
  actions?: TodoActionDTO[];
  drive_slug?: string | null;
}

// ============================================================================
// Shape converters — wire → frontend
// ============================================================================

/**
 * Derive the session's "current" section from its logged section rows.
 *
 * ``agendaOrder`` is the ordered list of kinds to scan for "next unstarted"
 * (and the source of the terminal fallback) — pass the team's ENABLED
 * effective-agenda kinds so a custom agenda's order/skip-disabled shape is
 * respected. Defaults to ``AGENDA_KINDS`` (the canonical 7-kind order) for
 * callers with no agenda in hand, which reproduces today's exact behavior.
 *
 * The "current" section is whichever one is started but not yet ended. If no
 * section is in that state, return the next un-started one in ``agendaOrder``.
 * Falls back to ``agendaOrder[0]`` (canonically "segue") for a brand-new
 * session with no sections logged yet — matches the mock's initial state.
 */
function deriveCurrentSection(
  sections: AgendaSectionWire[],
  agendaOrder: readonly AgendaKind[] = AGENDA_KINDS,
): AgendaKind {
  const active = sections.find((s) => s.started_at && !s.ended_at);
  if (active) return active.kind;
  const nextUnstarted = agendaOrder.find(
    (k) => !sections.some((s) => s.kind === k && s.started_at),
  );
  return nextUnstarted ?? agendaOrder[0] ?? "segue";
}

function snapshotToSession(
  snap: SessionSnapshotWire,
  agendaOrder: readonly AgendaKind[] = AGENDA_KINDS,
): L10Session {
  // Prefer the SESSION's snapshot agenda for the current-section derivation:
  // it arrives atomically with the section rows it's interpreting, so a
  // mid-meeting edit to the TEAM's agenda can never reshuffle a live
  // meeting's pointer (and the caller's possibly-stale order is irrelevant
  // whenever a snapshot exists).
  const snapshotOrder = snap.session.agenda
    ?.filter((item) => item.enabled)
    .map((item) => item.kind);
  const effectiveOrder =
    snapshotOrder && snapshotOrder.length > 0 ? snapshotOrder : agendaOrder;
  return {
    id: snap.session.id,
    team_id: snap.session.team_id,
    // facilitator_id on the wire can be null; the L10Session type holds a
    // string — fall back to empty string. The runner only uses it as the
    // ``actor_id`` default on event appends, which itself tolerates null.
    facilitator_id: snap.session.facilitator_id ?? "",
    status: snap.session.status,
    started_at: snap.session.started_at,
    ended_at: snap.session.ended_at,
    current_section: deriveCurrentSection(snap.sections, effectiveOrder),
    agenda: snap.session.agenda ?? undefined,
    sections: snap.sections.map(
      (s): L10AgendaSection => ({
        session_id: s.session_id,
        kind: s.kind,
        started_at: s.started_at,
        ended_at: s.ended_at,
        notes: s.notes ?? "",
      }),
    ),
    // Backend persists only present attendees (a row's existence is the
    // presence signal). Segue + Conclude treat missing entries as absent
    // by default — see segue-section's ``presentById.get(id) ?? false``.
    attendance: snap.attendance.map(
      (a): L10Attendance => ({
        session_id: a.session_id,
        profile_id: a.profile_id,
        present: true,
        rating_1_to_10: a.rating_1_to_10,
        rating_note: a.rating_note,
      }),
    ),
    events: snap.events,
    // Conclude Props shared cursor. Defaults mirror the model's server_defaults
    // so a pre-migration backend (or a session row that predates the column)
    // renders the landing screen rather than crashing.
    props_phase: snap.session.props_phase ?? "landing",
    props_index: snap.session.props_index ?? 0,
    props_review_started_at: snap.session.props_review_started_at ?? null,
    // IDS shared discussion focus — normalize the loose wire dict to the full
    // shape so consumers never null-check individual keys.
    ids_focus: snap.session.ids_focus
      ? {
          picking: snap.session.ids_focus.picking ?? false,
          picked: snap.session.ids_focus.picked ?? [],
          discussing: snap.session.ids_focus.discussing ?? [],
        }
      : null,
  };
}

function teamWireToTeam(t: TeamReadWire): L10Team {
  return {
    id: t.id,
    name: t.name,
    // Preserve null so an unscheduled team renders "No recurring schedule"
    // (matching the switcher cards) instead of a fabricated Monday-at-4PM
    // slot. ``scheduleLine`` returns the unscheduled label when either is null.
    recurring_day_of_week: t.recurring_day_of_week ?? null,
    recurring_time: t.recurring_time ? t.recurring_time.slice(0, 5) : null,
    recurring_tz: t.recurring_tz,
    default_duration_minutes: t.default_duration_minutes,
    section_budgets_seconds: {
      ...DEFAULT_SECTION_BUDGETS_SECONDS,
      ...(t.section_budgets_seconds as Record<AgendaKind, number>),
    },
    // Fall back to eos_weekly for any team whose row predates the migration
    // (server_default ensures new rows always carry a value, but old clients
    // talking to a just-deployed backend before migrate runs would see null).
    scorecard_kind: (SCORECARD_KINDS.includes(t.scorecard_kind as ScorecardKind)
      ? (t.scorecard_kind as ScorecardKind)
      : "eos_weekly"),
    meet_space_code: t.meet_space_code ?? null,
    scorecard_links: t.scorecard_links ?? [],
    scorecard_week_default: t.scorecard_week_default === "current_week" ? "current_week" : "last_week",
    brand_scope: t.brand_scope ?? [],
    agenda: t.agenda,
    agenda_is_custom: t.agenda_is_custom ?? false,
    container_kind: t.container_kind ?? "standard",
  };
}

/**
 * Strip the ``[ninety:headline:<id>]`` footer the Ninety importer
 * appends so its ``--clean-first`` pass can find rows to delete. The
 * marker is operational metadata, not user content — yanking it from
 * the wire-shape keeps prep-board / runner display clean without
 * losing the server-side ability to scope deletes by it.
 */
const NINETY_HEADLINE_FOOTER_RE = /\s*\[ninety:headline:[a-f0-9]+\]\s*$/i;
function stripNinetyHeadlineFooter(content: string): string {
  return content.replace(NINETY_HEADLINE_FOOTER_RE, "").trimEnd();
}

/**
 * Strip HTML tags from a Ninety-imported issue description.
 *
 * Ninety stores descriptions as Froala-edited HTML (``<p>`` /
 * ``<a target="_blank" data-fr-linked="true">…`` etc.); the importer
 * persists them verbatim into ``issues.description``. Rendering raw
 * HTML as text shows the tags literally; rendering via
 * ``dangerouslySetInnerHTML`` opens an XSS surface for any future
 * import that pulls untrusted content. Plain-text strip is the safe
 * middle ground for the prep-board's ``line-clamp-2`` excerpt.
 *
 * Uses ``DOMParser`` instead of a regex so we get correct entity
 * decoding (``&amp;`` → ``&``, ``&nbsp;`` → space, etc.) for free.
 * Sprint-2 follow-up: render trusted markup via DOMPurify so links
 * stay clickable instead of being flattened.
 */
function stripHtmlTags(html: string | null): string | null {
  if (!html) return html;
  // No tags? avoid the parser cost on the common manual-entry case.
  if (!html.includes("<")) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").trim();
}

function headlineWireToHeadline(h: HeadlineWire): Headline {
  // The body carries the rich text. Strip the Ninety operational footer
  // FIRST, then run the HTML strip against the result. Ninety headline
  // bodies arrive as "<p>…html body…</p>[ninety:headline:<id>]" —
  // strip-footer-then-HTML gives a clean plaintext body for both the
  // prep-board excerpt and the in-meeting Headlines pane. The title is a
  // plain string and passes through untouched.
  const rawBody = h.body ?? "";
  const withoutFooter = stripNinetyHeadlineFooter(rawBody);
  const plain = stripHtmlTags(withoutFooter) ?? withoutFooter;
  return {
    id: h.id,
    team_id: h.team_id,
    author_id: h.author_id ?? "",
    author_user_id: h.author_user_id ?? null,
    author_name: h.author_name ?? null,
    kind: h.kind,
    title: h.title,
    body: plain || null,
    sentiment: h.sentiment,
    cascaded_from_session_id: h.cascaded_from_session_id,
    cascaded_from_team_id: h.cascaded_from_team_id,
    cascaded_from_team_name: h.cascaded_from_team_name ?? null,
    linked_issue_id: h.linked_issue_id,
    dismissed_at: h.dismissed_at ?? null,
    read_at: h.read_at ?? null,
    source_type: h.source_type ?? null,
    source_ref: h.source_ref ?? null,
    // The drawer's "Last discussion summary" card renders off this.
    discussion_summary: h.discussion_summary ?? null,
    // Carry Drive artifact linkage through so HeadlineDrawer's unfurl
    // (gated on ``headline.artifact_token``) renders for staged headlines.
    artifact_token: h.artifact_token ?? null,
    dedup_key: h.dedup_key ?? null,
    // Drive feedback — the row renders the active vote off these, so an
    // uncarried field would silently blank everyone's thumbs on reload.
    my_feedback_rating: h.my_feedback_rating ?? null,
    feedback_counts: h.feedback_counts ?? null,
    created_at: h.created_at,
  };
}

function issueWireToIssue(i: IssueWire): Issue {
  return {
    ...i,
    // ``Issue.author_id`` is non-null on the frontend type but the wire
    // returns null when the author's profile was retired (SET NULL on
    // delete). Fall back to empty string so the runner can render
    // attribution as "unknown" instead of crashing.
    author_id: i.author_id ?? "",
    author_user_id: i.author_user_id ?? null,
    owner_user_id: i.owner_user_id ?? null,
    source_type: i.source_type ?? "manual",
    // Ninety descriptions arrive as Froala HTML — strip so the
    // prep-board's line-clamp excerpt shows readable text instead of
    // ``<p><a target="_blank" data-fr-linked="true">…``.
    description: stripHtmlTags(i.description),
  };
}

function mapTodoSource(source: string): L10UserTodo["source"] {
  // The frontend ``L10UserTodo.source`` union covers the origins we render
  // today: ``l10_meeting`` / ``sop_drive`` / ``drive`` / ``manual`` /
  // ``migrated_from_ninety``. Any other value (a future origin like
  // ``kory_chat``) collapses to ``manual`` until the union is widened —
  // collapsing to ``manual`` is intentional: it's the safe "we don't
  // recognize this provenance" fallback for the runner UI.
  if (
    source === "l10_meeting" ||
    source === "sop_drive" ||
    source === "drive" ||
    source === "manual" ||
    source === "migrated_from_ninety"
  ) {
    return source;
  }
  return "manual";
}

function todoReadToL10UserTodo(r: TodoReadWire): L10UserTodo {
  return {
    id: r.id,
    title: r.title,
    notes: r.notes,
    priority: r.priority,
    owner_id: r.owner_profile_id,
    owner_name: r.owner_name,
    due_date: r.due_date,
    status: r.status,
    source: mapTodoSource(r.source),
    completed_at: r.completed_at,
    completed_by_name: r.completed_by_name ?? null,
    origin_line: r.origin_line ?? null,
    created_by_name: r.created_by_name ?? null,
    reviewed_at: r.reviewed_at,
    dismissed_until: r.dismissed_until,
    l10_issue_id: r.l10_issue_id,
    l10_headline_id: r.l10_headline_id,
    linked_rock: r.linked_rock ?? null,
    l10_team_id: r.l10_team_id,
    l10_team_name: r.l10_team_name,
    created_at: r.created_at,
    delegated: r.delegated ?? false,
    // Carry Drive artifact linkage through so the shared to-do drawer's unfurl
    // (gated on ``todo.artifact_token``) renders for Drive-staged L10 to-dos.
    artifact_token: r.artifact_token ?? null,
    drive_dedup_key: r.drive_dedup_key ?? null,
    // Drive-staged rows: the Build executor + provenance chip. Omitting
    // these here silently strips them from every meeting surface (the
    // adapter can only forward what the mapper carries).
    actions: r.actions ?? [],
    drive_slug: r.drive_slug ?? null,
  };
}

function cascadeWireToCascade(c: CascadeDecisionWire): CascadeDecision {
  return {
    id: c.id,
    source_session_id: c.source_session_id,
    target_team_id: c.target_team_id,
    // Backend's create-cascade response populates this; snapshot reads
    // leave it absent. The runner toast + Conclude pane fall back to
    // the UUID when null — see ``CascadeDecision.target_team_name``.
    target_team_name: c.target_team_name ?? null,
    content: c.content,
    status: c.status,
    delivered_session_id: c.delivered_session_id,
    consumed_at: c.consumed_at,
    created_at: c.created_at,
  };
}

// ============================================================================
// API — team + session + agenda
// ============================================================================

export async function getTeam(teamId: string): Promise<L10Team> {
  const wire: TeamReadWire = await fetchApi(`/l10/teams/${teamId}`);
  return teamWireToTeam(wire);
}

// Exported so the screenshot harness (src/harness/fixtures.ts) can build
// demo `/l10/teams` payloads that type-check against the real wire shape.
export interface MyTeamWire {
  id: string;
  name: string;
  slug: string;
  tenant_id: string | null;
  recurring_day_of_week: number | null;
  recurring_time: string | null; // "HH:MM:SS"
  recurring_tz: string;
  default_duration_minutes: number;
  is_member: boolean;
  is_primary: boolean;
  has_activity: boolean;
  sealed: boolean;
  active_session_id: string | null;
  last_met_at: string | null;
  member_count: number;
  // See ``TeamReadWire.agenda`` — same always-populated / optional-on-the-wire
  // contract.
  agenda?: AgendaItem[];
  agenda_is_custom?: boolean;
  meet_space_code?: string | null;
}

/**
 * List the L10 teams the current user can run — backs the ``/meetings``
 * switcher and resolves a ``:teamSlug`` route param to a team id. Scoped
 * server-side by tenant visibility + team membership (see
 * ``GET /l10/teams``). The wire ``recurring_time`` ("HH:MM:SS") is trimmed
 * to "HH:MM" to match the ``L10Team`` convention.
 */
export async function listMyTeams(): Promise<MyL10Team[]> {
  const rows: MyTeamWire[] = await fetchApi(`/l10/teams`);
  return rows.map((t) => ({
    id: t.id,
    name: t.name,
    slug: t.slug,
    tenant_id: t.tenant_id,
    recurring_day_of_week: t.recurring_day_of_week,
    recurring_time: t.recurring_time ? t.recurring_time.slice(0, 5) : null,
    recurring_tz: t.recurring_tz,
    default_duration_minutes: t.default_duration_minutes,
    is_member: t.is_member,
    is_primary: t.is_primary,
    has_activity: t.has_activity,
    sealed: t.sealed,
    active_session_id: t.active_session_id,
    last_met_at: t.last_met_at,
    member_count: t.member_count,
    agenda: t.agenda,
    agenda_is_custom: t.agenda_is_custom ?? false,
    meet_space_code: t.meet_space_code ?? null,
  }));
}

export async function getAttendees(teamId: string): Promise<AttendeeProfile[]> {
  return fetchApi(`/l10/teams/${teamId}/members`);
}

/**
 * Add a profile to the team's roster. Returns the updated attendee list.
 * Idempotent — re-adding a member is a no-op.
 */
export async function addTeamMember(
  teamId: string,
  profileId: string,
): Promise<AttendeeProfile[]> {
  return fetchApi(`/l10/teams/${teamId}/members`, {
    method: "POST",
    body: JSON.stringify({ profile_id: profileId }),
  });
}

/**
 * Remove a profile from the team's roster. Idempotent — removing a
 * non-member returns 204.
 */
export async function removeTeamMember(
  teamId: string,
  profileId: string,
): Promise<void> {
  await fetchApi(`/l10/teams/${teamId}/members/${profileId}`, {
    method: "DELETE",
  });
}

/**
 * Set (or clear) a team as the CALLER'S home L10 — the self-serve sibling
 * of the admin member PATCH. The backend moves the single ``is_primary``
 * flag atomically, so setting a new home clears the old one server-side.
 */
export async function setMyHomeTeam(
  teamId: string,
  isPrimary: boolean,
): Promise<void> {
  await fetchApi(`/l10/teams/${teamId}/membership/self`, {
    method: "PATCH",
    body: JSON.stringify({ is_primary: isPrimary }),
  });
}

/** Remove the caller's own membership from a team ("Leave this L10"). */
export async function leaveMyTeam(teamId: string): Promise<void> {
  await fetchApi(`/l10/teams/${teamId}/membership/self`, {
    method: "DELETE",
  });
}

async function fetchSnapshot(
  sessionId: string,
  agendaOrder?: readonly AgendaKind[],
): Promise<L10Session> {
  const snap: SessionSnapshotWire = await fetchApi(`/l10/sessions/${sessionId}`);
  return snapshotToSession(snap, agendaOrder);
}

/**
 * ``agendaOrder`` — the team's ENABLED effective-agenda kinds, in order —
 * threads into ``current_section`` derivation so a custom agenda's
 * order/skip-disabled shape is respected. Omit for the canonical
 * ``AGENDA_KINDS`` fallback (every caller without a team's agenda in hand).
 */
export async function getSession(
  sessionId: string,
  agendaOrder?: readonly AgendaKind[],
): Promise<L10Session> {
  return fetchSnapshot(sessionId, agendaOrder);
}

/**
 * Fetch the conclude recap model for a session — the in-app preview of what
 * gets emailed to present attendees. Server-built from the same windowing the
 * email uses, so the screen and the email never disagree. The wire shape
 * matches ``L10Recap`` 1:1 (snake_case), so no mapping is needed.
 */
export async function getSessionRecap(sessionId: string): Promise<L10Recap> {
  return fetchApi(`/l10/sessions/${sessionId}/recap`);
}

/**
 * List a team's concluded L10 recaps (newest first) for the "Past recaps"
 * picker — discoverability for recaps you'd otherwise only reach via the recap
 * email/DM/URL. Lightweight summaries; opening one loads the full recap screen.
 */
export async function listTeamRecaps(teamId: string): Promise<L10RecapSummary[]> {
  return fetchApi(`/l10/teams/${teamId}/recaps`);
}

/** ``?series_id=`` for a Project session; the weekly L10 sends no query. */
function seriesQuery(seriesId: string | null | undefined): string {
  return seriesId ? `?series_id=${encodeURIComponent(seriesId)}` : "";
}

export async function getActiveSession(
  teamId: string,
  agendaOrder?: readonly AgendaKind[],
  seriesId?: string | null,
): Promise<L10Session | null> {
  // Read-only peek. Returns ``null`` when no session is in progress so
  // the runner page can render its landing screen with an explicit
  // ``[Start L10]`` CTA — auto-creating on mount was the beta-0 mock
  // semantic and made it impossible to surface "is a meeting actually
  // running right now" to the facilitator.
  const active: SessionReadWire | null = await fetchApi(
    `/l10/teams/${teamId}/sessions/active${seriesQuery(seriesId)}`,
  );
  if (!active) return null;
  return fetchSnapshot(active.id, agendaOrder);
}

/**
 * Explicitly start (or rejoin) the team's in-progress L10 session.
 *
 * Race-safe — backend serializes through ``SELECT … FOR UPDATE`` on the
 * ``l10_teams`` row so two browser tabs clicking [Start L10] at the
 * same time land on the same session row, not two split ones.
 */
export async function startMeeting(
  teamId: string,
  agendaOrder?: readonly AgendaKind[],
  seriesId?: string | null,
): Promise<L10Session> {
  const session: SessionReadWire = await fetchApi(
    `/l10/teams/${teamId}/sessions/ensure-active${seriesQuery(seriesId)}`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
  return fetchSnapshot(session.id, agendaOrder);
}

/** The team's meetings: its weekly L10 first, then its Project when it has one. */
export async function listTeamSeries(teamId: string): Promise<L10MeetingSeries[]> {
  return fetchApi(`/l10/teams/${teamId}/series`);
}

/** Add the team's Project meeting (idempotent). Gated on ``l10.manage``. */
export async function addProjectMeeting(teamId: string): Promise<L10MeetingSeries> {
  return fetchApi(`/l10/teams/${teamId}/series`, {
    method: "POST",
    body: JSON.stringify({ meeting_type: "project" }),
  });
}

/** Add the team's Sales Huddle (idempotent). Gated on ``l10.manage``. */
export async function addSalesHuddleMeeting(teamId: string): Promise<L10MeetingSeries> {
  return fetchApi(`/l10/teams/${teamId}/series`, {
    method: "POST",
    body: JSON.stringify({ meeting_type: "sales_huddle" }),
  });
}

/** The team's Sales Huddle and its brands. 404 when the team has not added one. */
export async function getTeamSalesHuddle(teamId: string): Promise<TeamSalesHuddle> {
  return fetchApi(`/l10/teams/${teamId}/sales-huddle`);
}

/** The Sales Huddles the caller can open (Sales Performance's Huddle tab). */
export async function listMySalesHuddles(): Promise<MySalesHuddle[]> {
  return fetchApi("/l10/sales-huddles");
}

/** Set (or ``{ reset: true }``) a Project meeting's agenda. Gated on ``l10.manage``. */
export async function updateSeriesAgenda(
  seriesId: string,
  data: { agenda: AgendaItem[]; default_duration_minutes: number } | { reset: true },
): Promise<L10MeetingSeries> {
  return fetchApi(`/l10/series/${seriesId}/agenda`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// The mutation-helpers below return ``void`` rather than re-fetching
// the full session snapshot. Callers run with TanStack Query optimistic
// updates (cache patched in ``onMutate``) and rely on the 2-second
// polling cycle to converge on server state. Dropping the per-mutation
// snapshot read halves the per-click wire cost — fast enough to feel
// instant on the runner pages.

export async function setCurrentSection(
  sessionId: string,
  kind: AgendaKind,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/sections/${kind}/start`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function endSection(
  sessionId: string,
  kind: AgendaKind,
  // Only the To-Dos section reads this — ``true`` stamps the team's
  // completed-but-unreviewed to-dos as reviewed (the facilitator's explicit
  // "Mark reviewed & continue" choice). Omitted/false ⇒ the backend never
  // stamps on section end, so advancing past To-Dos without marking leaves
  // ``reviewed_at`` untouched.
  markReviewed = false,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/sections/${kind}/end`, {
    method: "POST",
    body: JSON.stringify({ mark_reviewed: markReviewed }),
  });
}

export async function markPresent(
  sessionId: string,
  profileId: string,
  present: boolean,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/attendance/${profileId}/present`, {
    method: "POST",
    body: JSON.stringify({ present }),
  });
}

export async function setRating(
  sessionId: string,
  profileId: string,
  rating: number | null,
): Promise<void> {
  // `null` clears the rating — the endpoint accepts a nullable rating and the
  // repo upsert writes NULL, so the clear persists (no more refetch pop-back).
  await fetchApi(`/l10/sessions/${sessionId}/attendance/${profileId}/rating`, {
    method: "POST",
    body: JSON.stringify({ rating }),
  });
}

/** Set (or clear, with `null`) the "what would make it a 10?" note for an
 *  attendee's rating. Independent of the numeric rating write. */
export async function setRatingNote(
  sessionId: string,
  profileId: string,
  note: string | null,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/attendance/${profileId}/rating-note`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

/** The Conclude Props shared cursor as returned by the write endpoint. */
export interface PropsCursor {
  phase: PropsPhase;
  index: number;
  reviewStartedAt: string | null;
}

/**
 * Write the Conclude "Props" shared view-state cursor (last-write-wins).
 *
 * Any session participant may call this — it drives the ONE shared, mirrored
 * view-state on the Conclude screen. The clicking client updates optimistically
 * for instant feedback, then PATCHes; every other client mirrors the cursor off
 * the 2s session poll. Returns the persisted cursor (echoed back from the
 * session read).
 */
export async function setPropsCursor(
  sessionId: string,
  cursor: PropsCursor,
): Promise<PropsCursor> {
  const wire: SessionReadWire = await fetchApi(
    `/l10/sessions/${sessionId}/props-cursor`,
    {
      method: "PATCH",
      body: JSON.stringify({
        phase: cursor.phase,
        index: cursor.index,
        review_started_at: cursor.reviewStartedAt,
      }),
    },
  );
  return {
    phase: wire.props_phase ?? "landing",
    index: wire.props_index ?? 0,
    reviewStartedAt: wire.props_review_started_at ?? null,
  };
}

/** Write the IDS shared discussion focus (last-write-wins, any participant).
 *  Returns the normalized focus the server persisted. */
export async function setIdsFocus(
  sessionId: string,
  focus: IdsFocus,
): Promise<IdsFocus> {
  const wire: SessionReadWire = await fetchApi(
    `/l10/sessions/${sessionId}/ids-focus`,
    {
      method: "PATCH",
      body: JSON.stringify({
        picking: focus.picking,
        picked: focus.picked,
        discussing: focus.discussing,
      }),
    },
  );
  return {
    picking: wire.ids_focus?.picking ?? false,
    picked: wire.ids_focus?.picked ?? [],
    discussing: wire.ids_focus?.discussing ?? [],
  };
}

export async function endSession(sessionId: string): Promise<void> {
  const nowIso = new Date().toISOString();
  await fetchApi(`/l10/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ ended_at: nowIso, status: "completed" }),
  });
}

/**
 * Mark a meeting abandoned — preserves the session row + everything
 * created during it (issues / to-dos / headlines stay in the team's
 * queue) but transitions ``status`` to ``"abandoned"`` so it doesn't
 * show as in-progress for ``ensure-active`` lookups.
 *
 * Replaces Ninety's "navigate away and forget" pattern with an
 * explicit, audit-able close. Used by the topbar's
 * type-to-confirm Abandon button.
 */
export async function abandonSession(sessionId: string): Promise<void> {
  const nowIso = new Date().toISOString();
  await fetchApi(`/l10/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ ended_at: nowIso, status: "abandoned" }),
  });
}

/**
 * Append a ``session.paused`` event to the session's event log.
 *
 * Pause state is event-log derived — no schema change required. The
 * frontend walks the event stream to compute pause-adjusted elapsed
 * time on the total-meeting timer (per-section timers stay unpaused
 * for beta-1; sprint-2 polish).
 */
export async function pauseSession(sessionId: string): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/events`, {
    method: "POST",
    body: JSON.stringify({ kind: "session.paused", payload: {} }),
  });
}

/** Append a ``session.resumed`` event; symmetric to ``pauseSession``. */
export async function resumeSession(sessionId: string): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/events`, {
    method: "POST",
    body: JSON.stringify({ kind: "session.resumed", payload: {} }),
  });
}

/**
 * Append a ``todos.denominator_snapshot`` event — the locked denominator for
 * the runner's progress bar. First-event-wins: whoever enters the To-Dos
 * section first writes it; all clients (including the writer on their next
 * poll) read the earliest event of this kind. Duplicate posts are inert for
 * correctness (first-event-wins reducer), but the caller guards with a
 * pre-check to avoid log noise.
 *
 * Symmetric to ``pauseSession`` — same endpoint, free-text kind + JSONB
 * payload, no schema change needed.
 */
export async function snapshotTodoDenominator(
  sessionId: string,
  payload: TodoDenominatorPayload,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}/events`, {
    method: "POST",
    body: JSON.stringify({ kind: "todos.denominator_snapshot", payload }),
  });
}

/**
 * Hand the presenter role to another profile by reassigning the session's
 * ``facilitator_id`` (EOS "Claim Presenter"). The runner gates Pause/Abandon/
 * Finish on ``facilitator_id === my profile``, so this is the handoff. Assumes
 * the session PATCH accepts ``facilitator_id`` (same endpoint as end/abandon).
 */
export async function setFacilitator(
  sessionId: string,
  profileId: string,
): Promise<void> {
  await fetchApi(`/l10/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ facilitator_id: profileId }),
  });
}

// ============================================================================
// API — issues
// ============================================================================

export async function listIssues(teamId: string): Promise<Issue[]> {
  // Pull the full IDS queue in one call (limit caps at 2000 server-side).
  // The Ninety importer landed 324 issues for the leadership team day
  // one and the queue grows monotonically; the old 500 cap would have
  // silently truncated within a few cycles. Cursor pagination is a
  // sprint-2 follow-up once a team approaches 2000.
  const rows: IssueWire[] = await fetchApi(
    `/issues?team_id=${encodeURIComponent(teamId)}&limit=2000`,
  );
  return rows.map(issueWireToIssue);
}

export async function createIssue(body: CreateIssueBody): Promise<Issue> {
  // Base64 the free-text fields so the edge WAF doesn't 403 prose like "-->"
  // as an XSS signature; the backend decodes before validation. See
  // lib/content-encoding + app/core/content_encoding.
  const row: IssueWire = await fetchApi(`/issues`, {
    method: "POST",
    body: JSON.stringify({
      team_id: body.team_id,
      title: encodeBase64Utf8(body.title),
      description: body.description != null ? encodeBase64Utf8(body.description) : null,
      time_horizon: body.time_horizon,
      source_type: body.source_type,
      source_ref: body.source_ref ?? null,
      owner_id: body.owner_id ?? null,
      staged: body.staged ?? false,
      artifact_token: body.artifact_token ?? null,
      artifact_id: body.artifact_id ?? null,
      content_encoding: KORY_CONTENT_ENCODING,
    }),
  });
  return issueWireToIssue(row);
}

export async function updateIssueStatus(
  issueId: string,
  newStatus: Issue["status"],
): Promise<Issue> {
  const row: IssueWire = await fetchApi(`/issues/${issueId}`, {
    method: "PATCH",
    body: JSON.stringify({ status: newStatus }),
  });
  return issueWireToIssue(row);
}

export async function updateIssue(
  issueId: string,
  patch: UpdateIssueBody,
): Promise<Issue> {
  // Clearing the owner: the picker emits ``""`` for unassigned, but the
  // backend types ``owner_id`` as a UUID and 422s on an empty string. Send
  // ``null`` to clear; otherwise the picked profile id passes through and the
  // backend resolves it to a user.
  const body: Record<string, unknown> = { ...patch };
  if (body.owner_id === "") body.owner_id = null;
  // Base64 the free-text fields (only when a non-null string is being sent) so
  // the edge WAF doesn't 403 prose like "-->"; the backend decodes before
  // validation. See createIssue + lib/content-encoding.
  if (typeof body.title === "string") body.title = encodeBase64Utf8(body.title);
  if (typeof body.description === "string") body.description = encodeBase64Utf8(body.description);
  body.content_encoding = KORY_CONTENT_ENCODING;
  const row: IssueWire = await fetchApi(`/issues/${issueId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return issueWireToIssue(row);
}

export async function solveIssue(
  issueId: string,
  body: SolveIssueBody,
  sessionId?: string,
): Promise<{ issue: Issue; todo: L10UserTodo }> {
  // Two writes — mark the issue solved, then create the follow-up to-do.
  // Backend doesn't expose a single atomic "solve+todo" endpoint; the
  // runner's optimistic UI handles the in-between window. The to-do is
  // linked back to the issue via ``l10_issue_id``; the backend derives
  // the to-do's ``l10_team_id`` from that issue's team so it lands on the
  // team's runner panel — no client-supplied team/session needed.
  //
  // ``sessionId`` is still passed to the issue PATCH (it drives the
  // session event log) but is omitted when solving from the prep board
  // (no active session).
  // Base64 the free-text on both writes so the edge WAF doesn't 403 prose like
  // "-->"; the issue PATCH is decoded by IssueUpdateRequest and the inline to-do
  // POST by L10TodoCreateRequest (both set content_encoding). See createIssue.
  const issueRow: IssueWire = await fetchApi(`/issues/${issueId}`, {
    method: "PATCH",
    body: JSON.stringify({
      status: "solved",
      solve_summary: encodeBase64Utf8(body.solve_summary),
      content_encoding: KORY_CONTENT_ENCODING,
      ...(sessionId ? { session_id: sessionId } : {}),
    }),
  });
  const todoRow: TodoReadWire = await fetchApi(`/l10/todos`, {
    method: "POST",
    body: JSON.stringify({
      title: encodeBase64Utf8(body.todo.title),
      owner_profile_id: body.todo.owner_id,
      due_date: body.todo.due_date,
      l10_issue_id: issueId,
      content_encoding: KORY_CONTENT_ENCODING,
    }),
  });
  return { issue: issueWireToIssue(issueRow), todo: todoReadToL10UserTodo(todoRow) };
}

/**
 * Hard-delete an issue. Issues are L10-native (no engine generation) so any
 * team member may delete one; the backend gates on the issue's tenant scope.
 */
export async function deleteIssue(id: string): Promise<void> {
  await fetchApi(`/issues/${id}`, { method: "DELETE" });
}

/** Maximum ids per bulk-archive request (matches server ``max_length``). */
const BULK_ARCHIVE_CHUNK_SIZE = 500;

/**
 * Bulk-archive a set of backlog issues by stamping ``dismissed_at``.
 *
 * The client sends the explicit id set it previewed. The server re-validates
 * every id: cross-team ids, starred, staged, or already-archived issues are
 * skipped and counted in ``skipped_count``.
 *
 * Large sets are split into <=500-id chunks (matching the server cap) and
 * sent in parallel; results are summed. The endpoint is idempotent so
 * batching is safe.
 */
export async function bulkArchiveIssues(
  teamId: string,
  issueIds: string[],
): Promise<{ archived_count: number; skipped_count: number }> {
  const chunks: string[][] = [];
  for (let i = 0; i < issueIds.length; i += BULK_ARCHIVE_CHUNK_SIZE) {
    chunks.push(issueIds.slice(i, i + BULK_ARCHIVE_CHUNK_SIZE));
  }
  const results = await Promise.allSettled(
    chunks.map((chunk) =>
      fetchApi(
        `/issues/teams/${encodeURIComponent(teamId)}/bulk-archive`,
        { method: "POST", body: JSON.stringify({ issue_ids: chunk }) },
      ) as Promise<{ archived_count: number; skipped_count: number }>,
    ),
  );
  let archived_count = 0;
  let skipped_count = 0;
  for (const r of results) {
    if (r.status === "fulfilled") {
      archived_count += r.value.archived_count;
      skipped_count += r.value.skipped_count;
    } else {
      throw r.reason;
    }
  }
  return { archived_count, skipped_count };
}

/**
 * List archived issues for a team (``dismissed_at IS NOT NULL``).
 * Ordered most-recently-archived first.
 */
export async function listArchivedIssues(teamId: string): Promise<Issue[]> {
  const rows: IssueWire[] = await fetchApi(
    `/issues/teams/${encodeURIComponent(teamId)}/archived?limit=200`,
  );
  return rows.map(issueWireToIssue);
}

/**
 * Restore an archived issue by clearing ``dismissed_at``.
 */
export async function restoreIssue(
  issueId: string,
  teamId: string,
): Promise<Issue> {
  const row: IssueWire = await fetchApi(
    `/issues/${issueId}/restore?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return issueWireToIssue(row);
}

/**
 * Move an issue to one other L10 team (same id, new team). The backend
 * re-stamps the issue's tenant from the target team, clears staging, and
 * reconciles the owner. Returns the relocated row.
 */
export async function moveIssue(
  issueId: string,
  body: MoveIssueBody,
): Promise<Issue> {
  const row: IssueWire = await fetchApi(`/issues/${issueId}/move`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return issueWireToIssue(row);
}

/**
 * Copy an issue onto one or more other L10 teams. The source row stays in
 * place; each target gets a fresh, unassigned copy. Returns the new rows.
 */
export async function copyIssueTo(
  issueId: string,
  body: CopyIssueToBody,
): Promise<Issue[]> {
  const rows: IssueWire[] = await fetchApi(`/issues/${issueId}/copy`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return rows.map(issueWireToIssue);
}

export async function postScribeChunk(body: ScribeChunkBody): Promise<ScribeChunkResult> {
  // Multipart: the closed audio segment + the issue it was captured for. The
  // backend transcribes + merges it into that issue's discussion_summary.
  const form = new FormData();
  form.append("issue_id", body.issueId);
  form.append("start_ts", body.startTs);
  form.append("end_ts", body.endTs);
  const ext = body.blob.type.includes("mp4") ? "mp4" : "webm";
  form.append("audio", body.blob, `segment.${ext}`);
  return fetchApi(`/l10/sessions/${body.sessionId}/scribe/chunk`, {
    method: "POST",
    body: form,
  });
}

/**
 * Enqueue an async pull of the session's Google Meet transcript.
 *
 * Returns 202 immediately with the job status (queued/running/completed/failed).
 * Poll ``getTranscriptPullStatus`` for the final result.  The backend enforces
 * single-flight — a 409 means a pull is already in progress; the response body
 * carries the current status so the UI can update without a separate poll.
 *
 * Pre-flight errors (422) are still raised synchronously so the UI can display
 * them without polling.
 */
export async function pullMeetTranscript(
  sessionId: string,
  body: MeetTranscriptPullRequest = {},
): Promise<TranscriptPullStatus> {
  return fetchApi(`/l10/sessions/${sessionId}/pull-transcript`, {
    method: "POST",
    body: JSON.stringify({ organizer_email: body.organizerEmail ?? null }),
  });
}

/**
 * Poll the transcript pull job state for a session.
 *
 * Staleness guard applied server-side: a pull stuck in running for >15 minutes
 * is lazily reset to failed so the UI always eventually unblocks.
 */
export async function getTranscriptPullStatus(sessionId: string): Promise<TranscriptPullStatus> {
  return fetchApi(`/l10/sessions/${sessionId}/transcript-pull-status`);
}

/**
 * Is the session's team Meet live right now? Backs the runner's "Meet is live"
 * badge. The backend caches per Meet space, so polling this on a modest
 * interval from many viewers collapses to one upstream Google call per team.
 */
export async function getMeetStatus(sessionId: string): Promise<MeetSpaceStatus> {
  return fetchApi(`/l10/sessions/${sessionId}/meet-status`);
}

// ============================================================================
// API — headlines
// ============================================================================

export async function listHeadlines(teamId: string): Promise<Headline[]> {
  const rows: HeadlineWire[] = await fetchApi(
    `/headlines?team_id=${encodeURIComponent(teamId)}&limit=500`,
  );
  return rows.map(headlineWireToHeadline);
}

export async function createHeadline(body: CreateHeadlineBody): Promise<Headline> {
  // Base64 the free-text fields so the edge WAF doesn't 403 prose like "-->"
  // as an XSS signature; the backend decodes before validation.
  const row: HeadlineWire = await fetchApi(`/headlines`, {
    method: "POST",
    body: JSON.stringify({
      team_id: body.team_id,
      title: encodeBase64Utf8(body.title),
      body: body.body != null ? encodeBase64Utf8(body.body) : body.body,
      kind: body.kind,
      sentiment: body.sentiment,
      author_id: body.author_id ?? null,
      content_encoding: KORY_CONTENT_ENCODING,
      // Omit entirely when empty so a non-broadcast create sends the same
      // payload it always has (the backend defaults the field to []).
      ...(body.also_post_team_ids && body.also_post_team_ids.length > 0
        ? { also_post_team_ids: body.also_post_team_ids }
        : {}),
      // Issue-origin provenance (atomic — no create-then-PATCH needed).
      ...(body.linked_issue_id ? { linked_issue_id: body.linked_issue_id } : {}),
      // Todo-origin provenance.
      ...(body.source_type ? { source_type: body.source_type } : {}),
      ...(body.source_ref ? { source_ref: body.source_ref } : {}),
      // Kory artifact this headline wears as its body (create-from-artifact).
      ...(body.artifact_token ? { artifact_token: body.artifact_token } : {}),
      ...(body.artifact_id ? { artifact_id: body.artifact_id } : {}),
    }),
  });
  return headlineWireToHeadline(row);
}

/**
 * Update a headline's editable fields — title, body, kind, and/or
 * sentiment. Only the keys present in ``body`` are written; the backend
 * leaves the rest untouched. Used by the headline drawer's inline edits
 * (title/body on blur, kind/sentiment on chip-select).
 */
export async function updateHeadline(
  id: string,
  teamId: string,
  body: UpdateHeadlineBody,
): Promise<Headline> {
  // Clearing the author: the picker emits ``""``, but ``author_id`` is a
  // UUID server-side and 422s on an empty string — send ``null`` to clear.
  const wireBody: Record<string, unknown> = { ...body };
  if (wireBody.author_id === "") wireBody.author_id = null;
  // Base64 the free-text fields (only when a non-null string is being sent) so
  // the edge WAF doesn't 403 prose like "-->"; the backend decodes before
  // validation. See createHeadline + lib/content-encoding.
  if (typeof wireBody.title === "string") wireBody.title = encodeBase64Utf8(wireBody.title);
  if (typeof wireBody.body === "string") wireBody.body = encodeBase64Utf8(wireBody.body);
  wireBody.content_encoding = KORY_CONTENT_ENCODING;
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}?team_id=${encodeURIComponent(teamId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(wireBody),
    },
  );
  return headlineWireToHeadline(row);
}

/**
 * Archive a headline (soft-hide) — stamps ``dismissed_at`` server-side so
 * the row drops off the active list. ``restoreHeadline`` brings it back.
 * Team-scoped via ``?team_id=``, like ``updateHeadline``.
 */
export async function archiveHeadline(id: string, teamId: string): Promise<Headline> {
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}/archive?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return headlineWireToHeadline(row);
}

/** Restore an archived headline — clears ``dismissed_at`` server-side. */
export async function restoreHeadline(id: string, teamId: string): Promise<Headline> {
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}/restore?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return headlineWireToHeadline(row);
}

/**
 * Mark a headline read/announced — stamps ``read_at`` server-side
 * (team-wide). Distinct from archive: the row stays in the live Headlines
 * pane mid-meeting and only drops off the prep board afterward.
 * ``clearHeadlineRead`` clears it. Team-scoped via ``?team_id=``.
 */
export async function markHeadlineRead(id: string, teamId: string): Promise<Headline> {
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}/read?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return headlineWireToHeadline(row);
}

/** Mark a headline unread — clears ``read_at`` server-side. */
export async function clearHeadlineRead(id: string, teamId: string): Promise<Headline> {
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}/unread?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return headlineWireToHeadline(row);
}

/**
 * Hard-delete a headline. Team-scoped via ``?team_id=`` like
 * ``updateHeadline`` / ``archiveHeadline``. Headlines are L10-native so any
 * team member may delete one.
 */
export async function deleteHeadline(id: string, teamId: string): Promise<void> {
  await fetchApi(`/headlines/${id}?team_id=${encodeURIComponent(teamId)}`, {
    method: "DELETE",
  });
}

/**
 * Move a headline to one other L10 team (same id, new team). Source-scoped via
 * ``?team_id=`` like ``updateHeadline``; a headline has no tenant to re-stamp.
 * Returns the relocated row.
 */
export async function moveHeadline(
  id: string,
  teamId: string,
  body: MoveHeadlineBody,
): Promise<Headline> {
  const row: HeadlineWire = await fetchApi(
    `/headlines/${id}/move?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return headlineWireToHeadline(row);
}

/**
 * Copy a headline onto one or more other L10 teams. The source row stays in
 * place; each target gets a ``kind='cascade'`` copy back-linked to the source.
 * Source-scoped via ``?team_id=``. Returns the new rows.
 */
export async function copyHeadlineTo(
  id: string,
  teamId: string,
  body: CopyHeadlineToBody,
): Promise<Headline[]> {
  const rows: HeadlineWire[] = await fetchApi(
    `/headlines/${id}/copy?team_id=${encodeURIComponent(teamId)}`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return rows.map(headlineWireToHeadline);
}

// ============================================================================
// API — to-dos
// ============================================================================

export async function listTodos(teamId: string): Promise<L10UserTodo[]> {
  const rows: TodoReadWire[] = await fetchApi(`/l10/teams/${teamId}/todos`);
  return rows.map(todoReadToL10UserTodo);
}

// ============================================================================
// API — in-meeting search (todos + headlines)
// ============================================================================

export interface MeetingSearchResult {
  kind: "todo" | "headline";
  id: string;
  title: string;
  snippet: string | null;
  owner_name: string | null;
  team_id: string | null;
  in_current_meeting: boolean;
  completed: boolean;
  created_at: string;
}

/**
 * Search todos + headlines the caller can see during a meeting: items in the
 * current team, assigned to them, or assigned-to/created-by a teammate.
 */
export async function searchMeetingItems(teamId: string, query: string): Promise<MeetingSearchResult[]> {
  return fetchApi(`/l10/teams/${teamId}/search?q=${encodeURIComponent(query)}`);
}

/**
 * Create a standalone to-do — used by the pre-meeting prep board so
 * users can queue items between L10s without being inside an active
 * session. The session-bound Solve path inlines its own POST instead of
 * reusing this helper because it also has to PATCH the issue atomically
 * (see ``solveIssue``).
 *
 * The caller passes ``l10_team_id`` (the team this to-do belongs to);
 * ``list_team_todos`` scopes the team's panel on that first-class column.
 */
export async function createTodo(body: CreateTodoBody): Promise<L10UserTodo> {
  // Base64 the free-text fields so the edge WAF doesn't 403 prose like "-->"
  // as an XSS signature; the backend decodes before validation.
  const row: TodoReadWire = await fetchApi(`/l10/todos`, {
    method: "POST",
    body: JSON.stringify({
      title: encodeBase64Utf8(body.title),
      owner_profile_id: body.owner_profile_id,
      due_date: body.due_date ?? null,
      l10_team_id: body.l10_team_id ?? null,
      l10_issue_id: body.l10_issue_id ?? null,
      l10_headline_id: body.l10_headline_id ?? null,
      // Only sent when set — an unlinked create stays byte-identical.
      ...(body.rock_id ? { rock_id: body.rock_id } : {}),
      notes: body.notes != null ? encodeBase64Utf8(body.notes) : null,
      content_encoding: KORY_CONTENT_ENCODING,
      // Only sent when set — the backend defaults the column to "normal".
      ...(body.priority ? { priority: body.priority } : {}),
      ...(body.group !== undefined ? { group: body.group } : {}),
      // Kory artifact this to-do wears as its body (create-from-artifact).
      ...(body.artifact_token ? { artifact_token: body.artifact_token } : {}),
      ...(body.artifact_id ? { artifact_id: body.artifact_id } : {}),
    }),
  });
  return todoReadToL10UserTodo(row);
}

export async function toggleTodo(
  todoId: string,
  done: boolean,
  teamId: string,
): Promise<L10UserTodo> {
  // Team-scoped URL: the backend authz's against the team in the path
  // and verifies the to-do actually belongs to that team. Earlier
  // versions hit ``/l10/todos/{id}/toggle`` and tried to reverse the
  // team out of the ``todo_key`` — that broke for Ninety-imported
  // rows whose key carries Ninety's team id, not the HickoryOS UUID.
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/toggle`,
    {
      method: "POST",
      body: JSON.stringify({ done }),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Update a to-do's due-date — the L10 "push another week" gesture. Pass
 * a ``YYYY-MM-DD`` string or ``null`` to clear. Backend writes the real
 * ``due_at`` column (leaving ``subtitle`` notes untouched); see
 * ``set_todo_due_date`` in the repo.
 */
export async function setTodoDueDate(
  todoId: string,
  dueDate: string | null,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/due-date`,
    {
      method: "POST",
      body: JSON.stringify({ due_date: dueDate }),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Edit a to-do's title / notes / priority — the L10 drawer's inline
 * field edits the due-date / snooze endpoints don't cover. Only the
 * provided fields are sent (and applied server-side). ``notes`` maps to
 * the row's ``subtitle`` column; ``priority`` is the ``normal`` /
 * ``high`` / ``critical`` string. Same team-scoped path + ownership
 * guard as ``setTodoDueDate``.
 */
export async function updateTodo(
  todoId: string,
  patch: {
    title?: string;
    notes?: string;
    priority?: "normal" | "high" | "critical";
    group?: string | null;
    artifact_token?: string | null;
    artifact_id?: string | null;
  },
  teamId: string,
): Promise<L10UserTodo> {
  // Base64 the free-text fields (only when a non-null string is being sent) so
  // the edge WAF doesn't 403 prose like "-->"; the backend decodes before
  // validation. See createTodo + lib/content-encoding.
  const body: Record<string, unknown> = { ...patch };
  if (typeof body.title === "string") body.title = encodeBase64Utf8(body.title);
  if (typeof body.notes === "string") body.notes = encodeBase64Utf8(body.notes);
  body.content_encoding = KORY_CONTENT_ENCODING;
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Back-link a to-do to an issue that was created from it (To-Do→Issue
 * cross-create). Writes ``l10_issue_id`` on the to-do row; the to-do stays
 * open — this only sets the FK so the existing to-do↔issue provenance UI
 * lights up. Fire-and-forget from the issue composer's post-create callback.
 */
export async function linkTodoToIssue(
  todoId: string,
  issueId: string,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ l10_issue_id: issueId }),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Reassign a to-do to another teammate. Goes through the same
 * team-scoped to-do PATCH as ``updateTodo`` (the assignee picker is
 * editable in L10 because the backend resolves ``owner_profile_id`` →
 * ``user_id`` and moves the row). L10 keys owners on ``profile.id`` (the
 * agent surface keys on ``profile.user_id`` instead). A profile with no
 * linked login is a 422 server-side — a to-do needs an inbox to land in.
 */
export async function reassignTodo(
  todoId: string,
  ownerProfileId: string,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ owner_profile_id: ownerProfileId }),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Move a TEAM-BOUND to-do to one other L10 team (same id, new team) — the
 * to-do analogue of ``moveIssue``. The backend re-stamps the to-do's team and
 * returns the relocated row. ``teamId`` is the to-do's CURRENT team (the move
 * source). A team-LESS to-do routes through ``assignTodo`` instead.
 */
export async function moveTodo(
  teamId: string,
  todoId: string,
  targetTeamId: string,
): Promise<L10UserTodo> {
  const body: MoveTodoBody = { target_team_id: targetTeamId };
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/move`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Copy a to-do onto one or more other L10 teams — the source row stays in
 * place; each target gets a fresh copy. Returns the new rows. ``teamId`` is
 * the to-do's CURRENT team.
 */
export async function copyTodoTo(
  teamId: string,
  todoId: string,
  targetTeamIds: string[],
): Promise<L10UserTodo[]> {
  const body: CopyTodoToBody = { target_team_ids: targetTeamIds };
  const rows: TodoReadWire[] = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/copy`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return rows.map(todoReadToL10UserTodo);
}

/**
 * Bind a TEAM-LESS (Overview) to-do to an L10 — the core fix that lets an
 * arbitrary Overview to-do be routed into a meeting. The endpoint is NOT
 * team-scoped (the to-do has no team yet) and 409s if the to-do is already
 * team-bound, so the FE picks this vs ``moveTodo`` off ``l10_team_id``.
 */
export async function assignTodo(
  todoId: string,
  targetTeamId: string,
): Promise<L10UserTodo> {
  const body: AssignTodoBody = { target_team_id: targetTeamId };
  const row: TodoReadWire = await fetchApi(`/l10/todos/${todoId}/assign`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return todoReadToL10UserTodo(row);
}

/**
 * Snooze a to-do until ``until`` (a ``YYYY-MM-DD`` string) — hides the
 * row from the active list until that date by stamping
 * ``dismissed_until`` server-side. Same team-scoped path + ownership
 * guard as ``toggleTodo`` / ``setTodoDueDate``.
 */
export async function snoozeTodo(
  todoId: string,
  until: string,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/snooze`,
    {
      method: "POST",
      body: JSON.stringify({ snooze_until: until }),
    },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Archive a to-do (permanent hide) — stamps ``dismissed_at`` server-side.
 * ``restoreTodo`` brings it back. Same team-scoped path + ownership
 * guard as ``toggleTodo``.
 */
export async function archiveTodo(
  todoId: string,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/archive`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Restore a snoozed / archived to-do — clears ``dismissed_at`` +
 * ``dismissed_until`` server-side so the row rejoins the active list.
 */
export async function restoreTodo(
  todoId: string,
  teamId: string,
): Promise<L10UserTodo> {
  const row: TodoReadWire = await fetchApi(
    `/l10/teams/${teamId}/todos/${todoId}/restore`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return todoReadToL10UserTodo(row);
}

/**
 * Hard-delete a team to-do. SOP-generated to-dos (``source === 'sop_drive'``)
 * are engine-owned and reject server-side with 404 — only L10-native to-dos
 * are deletable.
 */
export async function deleteTodo(todoId: string, teamId: string): Promise<void> {
  await fetchApi(`/l10/teams/${teamId}/todos/${todoId}`, { method: "DELETE" });
}

/** Remove a to-do's rock link (the drawer's "Remove link to rock"). 204 on an
 *  unlinked to-do; 404 when the rock is outside the caller's rock scope. */
export async function unlinkTodoRock(todoId: string, teamId: string): Promise<void> {
  await fetchApi(`/l10/teams/${teamId}/todos/${todoId}/rock-link`, { method: "DELETE" });
}

// ============================================================================
// API — cascades
// ============================================================================

/**
 * List cascades produced *by* a session — what this team is sending out.
 * The Conclude pane filters by ``source_session_id === sessionId`` to
 * scope the display to the current meeting (the mock returned ALL
 * cascades; the real client takes the session id explicitly).
 */
export async function listCascades(sessionId?: string): Promise<CascadeDecision[]> {
  if (!sessionId) return [];
  // The session snapshot already includes cascades sourced from this
  // session, so we ride on it instead of adding a duplicate endpoint.
  const snap: SessionSnapshotWire = await fetchApi(`/l10/sessions/${sessionId}`);
  return snap.cascades.map(cascadeWireToCascade);
}

export async function createCascade(body: CreateCascadeBody): Promise<CascadeDecision> {
  const row: CascadeDecisionWire = await fetchApi(`/cascades`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  // The backend populates ``target_team_name`` on this response from the
  // target team it already loaded for authz — no client-side join
  // needed. Snapshot reads leave it null and the Conclude pane falls
  // back to the UUID; the create-flow toast always has the real name.
  return cascadeWireToCascade(row);
}

/**
 * List active L10 teams that the current team can cascade headlines to.
 * Excludes the current team server-side. Returns ``[]`` when no
 * ``teamId`` is supplied (caller forgot to pass it through) — same
 * "fail-soft empty" pattern as ``listCascades``.
 */
export async function listCascadeTargets(
  teamId?: string,
): Promise<{ id: string; name: string }[]> {
  if (!teamId) return [];
  return fetchApi(`/l10/teams/${teamId}/cascade-targets`);
}

// ============================================================================
// API — scorecard + rocks (deferred — empty stubs for beta-1)
// ============================================================================

/**
 * Per-team scorecard rows for the L10 SCORECARD embed.
 *
 * **Deferred to a follow-up PR** — the existing scorecard endpoints
 * aren't shaped for the L10 weekly-grid view, and wiring a per-team
 * subset is its own ticket. Returns ``[]`` so the runner renders the
 * SCORECARD panel's empty state cleanly; the meeting can still proceed
 * (the section is skippable). Tracked as a sprint-2 wire-up.
 */
export async function getScorecardForTeam(_teamId: string): Promise<ScorecardRow[]> {
  return [];
}

/**
 * Per-team weekly rock snapshots for the L10 ROCKS embed.
 *
 * **Deferred to a follow-up PR** — same reasoning as
 * ``getScorecardForTeam``. The /rocks endpoints exist but don't bundle
 * weekly status + progress in the shape the L10 panel needs. Beta-1
 * renders an empty rocks panel and the facilitator skips to headlines.
 */
export async function getRocksForTeam(_teamId: string): Promise<RockWeeklySnapshot[]> {
  return [];
}

// ============================================================================
// API — generic item-link substrate
// ============================================================================

/**
 * Create (or idempotently return) a generic link between two L10 items.
 *
 * Both items must belong to ``teamId``. The pair is normalized server-side
 * so calling with (a, b) or (b, a) returns the same row. Idempotent —
 * re-linking returns the existing link.
 *
 * Cells that already have dedicated FKs (headline→issue via
 * ``Headline.linked_issue_id``, todo→issue via ``UserTodo.l10_issue_id``,
 * etc.) should NOT be double-written here.
 */
export async function createItemLink(
  teamId: string,
  itemA: L10ItemRef,
  itemB: L10ItemRef,
): Promise<L10ItemLink> {
  return fetchApi(`/l10/teams/${encodeURIComponent(teamId)}/item-links`, {
    method: "POST",
    body: JSON.stringify({ item_a: itemA, item_b: itemB }),
  });
}

/**
 * List all item-links touching the given item on the team.
 *
 * Returns ``[]`` when the item has no links. Each row includes the other
 * item's resolved title so the UI needs no N+1 lookups.
 *
 * Pass ``source="manual"`` (the chip-row default) to exclude system
 * provenance back-pointers, which are write-side plumbing for a future FK
 * removal and must not appear as user-removable chips.
 */
export async function listItemLinks(
  teamId: string,
  kind: L10ItemKind,
  id: string,
  source?: "manual" | "provenance",
): Promise<L10ItemLink[]> {
  const qs = new URLSearchParams({ kind, id });
  if (source !== undefined) qs.set("source", source);
  return fetchApi(
    `/l10/teams/${encodeURIComponent(teamId)}/item-links?${qs.toString()}`,
  );
}

/**
 * Delete an item-link by its id. Returns 204 No Content on success.
 */
export async function deleteItemLink(
  teamId: string,
  linkId: string,
): Promise<void> {
  await fetchApi(
    `/l10/teams/${encodeURIComponent(teamId)}/item-links/${encodeURIComponent(linkId)}`,
    { method: "DELETE" },
  );
}
